/**
 * Blocks v4 hosted SSO (authorization-code flow).
 *
 * Replaces the v1 password-grant + JS-held-JWT model. v4 login is hosted:
 *   1. GET  /iam/v4/idp/initiate  → `{ redirect_uri }` (Blocks builds state/nonce/PKCE)
 *   2. hand off to that URL (web: navigate · native: in-app auth session)
 *   3. Blocks redirects back to our registered redirectUri with ?code&state
 *   4. GET  /iam/v4/idp/callback?code&state  → sets the session cookie
 *
 * The session lives in an HttpOnly cookie, so JS never holds the access token.
 * On native the cookie lands in the platform cookie store (NSHTTPCookieStorage /
 * OkHttp), which RN's fetch then sends automatically to the same host — so the
 * browser-only "same registrable domain" constraint applies to web builds only.
 */

import * as Linking from "expo-linking";
import * as WebBrowser from "expo-web-browser";
import { Platform } from "react-native";
import {
  buildRedirectUri,
  pickSameOriginRedirectUri,
  SOCIAL_CALLBACK_PATH,
  SSO_CALLBACK_PATH,
} from "@/src/utils/sso-redirect.utils";
import {
  BLOCKS_ENDPOINTS,
  OIDC_CLIENT_ID,
  PROJECT_KEY,
  SOCIAL_CLIENT_ID,
  SSO_REDIRECT_URI,
  SSO_REDIRECT_URI_OVERRIDE,
  blocksHeaders,
  isSsoConfigured,
} from "./config";
import { clearMeCache } from "./session";

const isWeb = Platform.OS === "web";

/**
 * Which flow minted the code we come back with. The two flows have DIFFERENT
 * exchange endpoints and each rejects the other's state, so the callback screen
 * has to know which one it is finishing.
 *
 * On web the browser leaves the app entirely between start and callback, so the
 * marker has to outlive the JS context — `sessionStorage` does, a module
 * variable does not. Native never needs it (the code/state come straight back
 * from the auth session), but it is kept in a module variable there so the
 * accessor works on both.
 */
export type SsoFlow = "social" | "oidc";
const SSO_FLOW_KEY = "blocks_sso_flow";
let lastFlow: SsoFlow | null = null;

const rememberSsoFlow = (flow: SsoFlow): void => {
  lastFlow = flow;
  try {
    if (isWeb) window.sessionStorage?.setItem(SSO_FLOW_KEY, flow);
  } catch {
    // Private-mode / blocked storage — `getSsoFlow` falls back to "social".
  }
};

/**
 * The flow that started the sign-in now finishing. Defaults to `"social"`: it is
 * the configured provider and the only flow a user can reach from the login
 * screen, so an absent marker (storage blocked, or a link reopened in a fresh
 * tab) is far likelier to be a social login than a hosted one.
 */
export const getSsoFlow = (): SsoFlow => {
  try {
    if (isWeb) {
      const stored = window.sessionStorage?.getItem(SSO_FLOW_KEY);
      if (stored === "social" || stored === "oidc") return stored;
    }
  } catch {
    // fall through
  }
  return lastFlow ?? "social";
};

/** Called once the exchange is done, so a later visit cannot reuse the marker. */
export const clearSsoFlow = (): void => {
  lastFlow = null;
  try {
    if (isWeb) window.sessionStorage?.removeItem(SSO_FLOW_KEY);
  } catch {
    // nothing to clear
  }
};

/**
 * The social provider name clicked by the user, persisted across the web
 * redirect so `finishSocialLogin` exchanges with the right provider name.
 * Follows the same pattern as the SSO_FLOW_KEY above.
 */
const SSO_PROVIDER_KEY = "blocks_sso_provider";
let lastProvider: string | null = null;

const rememberSsoProvider = (name: string): void => {
  lastProvider = name;
  try {
    if (isWeb) window.sessionStorage?.setItem(SSO_PROVIDER_KEY, name);
  } catch {
    // Private-mode / blocked storage — getSsoProvider falls back to lastProvider.
  }
};

const getSsoProvider = (): string | null => {
  try {
    if (isWeb) {
      const stored = window.sessionStorage?.getItem(SSO_PROVIDER_KEY);
      if (stored) return stored;
    }
  } catch {
    // fall through
  }
  return lastProvider;
};

const clearSsoProvider = (): void => {
  lastProvider = null;
  try {
    if (isWeb) window.sessionStorage?.removeItem(SSO_PROVIDER_KEY);
  } catch {
    // nothing to clear
  }
};

export type SocialProvider = Readonly<{
  provider: string;
  displayName?: string;
  icon?: string;
  clientId: string;
  redirectUris: readonly string[];
}>;

// Distinguishes a successful (possibly empty) response from a network/HTTP failure
// so getSocialProviders() can apply the env-var fallback only on actual failure.
type ProviderListResult =
  | { ok: true; providers: SocialProvider[] }
  | { ok: false };

let providerListCache: Promise<ProviderListResult> | null = null;

/**
 * Fetch the provider list at most once per app load. Returns the parsed list
 * on success, or `{ ok: false }` on any network or HTTP error. The result is
 * cached so every caller shares one request.
 */
const fetchProviderList = (): Promise<ProviderListResult> => {
  providerListCache ??= (async () => {
    try {
      const res = await fetch(BLOCKS_ENDPOINTS.loginOptions, {
        headers: blocksHeaders(),
      });
      if (!res.ok) return { ok: false };

      const { ssoInfo } = (await res.json()) as {
        ssoInfo?: {
          provider?: string;
          displayName?: string;
          icon?: string;
          clientId?: string;
          redirectUris?: string[];
        }[];
      };

      const providers = (ssoInfo ?? [])
        .filter((p) => p.provider && p.clientId)
        .map((p) => ({
          provider: p.provider as string,
          displayName: p.displayName,
          icon: p.icon,
          clientId: p.clientId as string,
          redirectUris: p.redirectUris ?? [],
        }));

      return { ok: true, providers };
    } catch {
      return { ok: false };
    }
  })();
  return providerListCache;
};

/**
 * All social providers enabled for this project, discovered from
 * `/auth/login-options` (unauthenticated — verified live 2026-08-02).
 *
 * On a successful response returns the full list (may be empty if no providers
 * are configured). On a fetch failure falls back to a synthetic Microsoft
 * provider built from `EXPO_PUBLIC_BLOCKS_SOCIAL_CLIENT_ID` when that env var
 * is set, preserving the original behaviour. When neither condition holds,
 * returns an empty array and the login screen shows no SSO buttons.
 */
/**
 * The provider `/auth/login-options` is expected to return when it is reachable.
 *
 * It is 401 while signed out, so on a cold sign-in discovery fails and the env
 * `SOCIAL_CLIENT_ID` carries the flow instead. That fallback used to rescue only
 * the *start* of the login — see `startSocialLogin`.
 */
export const FALLBACK_SOCIAL_PROVIDER = "microsoft";

export const getSocialProviders = async (): Promise<SocialProvider[]> => {
  const result = await fetchProviderList();
  if (!result.ok) {
    if (SOCIAL_CLIENT_ID) {
      return [
        {
          provider: FALLBACK_SOCIAL_PROVIDER,
          displayName: FALLBACK_SOCIAL_PROVIDER,
          icon: FALLBACK_SOCIAL_PROVIDER,
          clientId: SOCIAL_CLIENT_ID,
          // Mirrors how the provider is actually registered, so the fallback
          // path produces the same redirectUri as a successful discovery.
          redirectUris: [getSocialRedirectUri()],
        },
      ];
    }
    return [];
  }
  return result.providers;
};

/**
 * Discover the project's first social provider from `/auth/login-options`.
 *
 * Returns `null` rather than throwing — the caller falls back to the configured
 * `SOCIAL_CLIENT_ID`, so a hiccup here degrades to the old behaviour instead of
 * taking sign-in down.
 */
export const getSocialProvider = async (): Promise<SocialProvider | null> => {
  const result = await fetchProviderList();
  if (!result.ok) return null;
  return result.providers[0] ?? null;
};

/**
 * Pick the redirect URI to ask for.
 *
 * It must match a `redirectUris` entry on the provider record exactly, AND its
 * origin must be one the project allows (an unlisted origin gets
 * `406 Invalid_Origin_Or_Referer` from the API and sign-in never starts). So:
 * an explicit override wins; otherwise prefer the registered entry that matches
 * where the app is actually running — `<origin>/login` first, since the native
 * app's callback shares the host; otherwise take the first registered entry
 * and let the provider send the user to whichever host it knows about.
 */
const pickRedirectUri = (
  registered: readonly string[],
  fallback: () => string = SSO_REDIRECT_URI,
): string => {
  if (SSO_REDIRECT_URI_OVERRIDE) return SSO_REDIRECT_URI_OVERRIDE;

  const origin =
    isWeb && typeof window !== "undefined" ? window.location?.origin : "";
  if (origin) {
    const sameOrigin = pickSameOriginRedirectUri(registered, origin);
    if (sameOrigin) return sameOrigin;
  }

  return registered[0] ?? fallback();
};

/** Callback path — must be registered on the OIDC client for BOTH platforms. */
export { SSO_CALLBACK_PATH, SOCIAL_CALLBACK_PATH } from "@/src/utils/sso-redirect.utils";

/**
 * Where the **social** provider sends the browser back — NOT the same as
 * `SSO_CALLBACK_PATH`.
 *
 * The Microsoft provider is registered against `<origin>/login`, while the
 * hosted `blocks-oidc` client uses `/auth/callback`. `social/initiate` does not
 * validate the `redirectUri` it is handed — it echoes it into the authorize URL
 * — so sending the OIDC path on the social flow produces a code that cannot be
 * redeemed, and it fails at the callback rather than at sign-in.
 *
 * That is exactly what happened when `/auth/login-options` 401s while signed
 * out: the env-fallback provider carries no `redirectUris`, so the picker fell
 * through to the OIDC path and the first sign-in attempt always failed. The
 * second attempt worked only because discovery could succeed by then and supply
 * the registered `/login`.
 */
export const getSocialRedirectUri = (): string => {
  if (isWeb) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return buildRedirectUri(origin, SOCIAL_CALLBACK_PATH);
  }
  return Linking.createURL(SOCIAL_CALLBACK_PATH);
};

/**
 * Web resolves to an https URL on the app's own origin; native resolves to the
 * app's deep link (`l3reactseliseclub://auth/callback`, or an `exp://…` URL under
 * Expo Go). Both variants must appear in the OIDC client's `redirectUris`.
 */
export const getRedirectUri = (): string => {
  if (isWeb) {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    return buildRedirectUri(origin, SSO_CALLBACK_PATH);
  }
  return Linking.createURL(SSO_CALLBACK_PATH);
};

export class SsoConfigError extends Error {}

/**
 * Fetch the social authorize URL for the given provider.
 *
 * Does not call getSocialProvider internally — the caller resolves which
 * provider to use and passes it in. Falls back to `SOCIAL_CLIENT_ID` if no
 * provider is given (env-var fallback path).
 *
 * This is the flow that matches how the provider is actually configured
 * (`providerType: "social"`), not the hosted `blocks-oidc` one further down.
 * Contract verified live 2026-07-30:
 *
 *   GET /auth/social/initiate?clientId=<provider clientId>&redirectUri=<uri>
 *     → 200 { authorizationUrl }
 *
 * Three sharp edges, all measured — none of them are guesses, and each one cost
 * real time to find:
 *
 * 1. `clientId` is the **identity provider's** clientId, not the blocks-oidc
 *    client id. The latter answers `400 invalid_provider_type`.
 * 2. The returned `authorizationUrl` (`/api/oidc/authorize`) **cannot be
 *    navigated to**: it requires the `x-blocks-key` header, which a top-level
 *    navigation cannot send, so the browser would land on
 *    `404 Application_Not_Found`. It 302s to `/oidc/login?<same query>`, which
 *    loads without any header — so that is what we send the browser to, via the
 *    string replace at the end of this function. The query string carries over
 *    verbatim, `state` included, so the state Blocks minted at initiate is
 *    preserved. **Do not "simplify" that replace away.**
 * 3. `social/initiate` does NOT validate `redirectUri` against the provider's
 *    registered list — it echoes whatever it is given straight into the
 *    authorize URL (measured 2026-08-02), so a wrong value fails later and much
 *    less obviously. That list comes from `/auth/login-options`; the Microsoft
 *    provider is registered with `https://dtggna-dqrbx.slsblx.com/login`, so the
 *    browser comes back to the LOGIN screen with `?code&state` (see use-login.ts).
 *
 * It also reports `requirePkce: false`, so no `code_challenge` is expected here.
 */
const getSocialAuthorizeUrl = async (provider?: SocialProvider): Promise<string> => {
  const clientId = provider?.clientId || SOCIAL_CLIENT_ID;

  if (!clientId) {
    throw new SsoConfigError(
      "No social identity provider configured — /auth/login-options returned none and EXPO_PUBLIC_BLOCKS_SOCIAL_CLIENT_ID is unset.",
    );
  }

  const url =
    `${BLOCKS_ENDPOINTS.socialInitiate}` +
    `?clientId=${encodeURIComponent(clientId)}` +
    `&redirectUri=${encodeURIComponent(
      pickRedirectUri(provider?.redirectUris ?? [], getSocialRedirectUri),
    )}`;

  const res = await fetch(url, { headers: blocksHeaders() });
  if (!res.ok) {
    throw new Error(`social/initiate failed: ${res.status}`);
  }

  const { authorizationUrl } = (await res.json()) as {
    authorizationUrl?: string;
  };
  if (!authorizationUrl) {
    throw new Error("social/initiate returned no authorizationUrl");
  }

  // Swap the header-gated API path for the browser-navigable hosted page,
  // keeping the query (and therefore the state) exactly as issued.
  return authorizationUrl.replace("/api/oidc/authorize", "/oidc/login");
};

/**
 * Log in through a social identity provider.
 *
 * Accepts an optional specific `provider`; when omitted, uses the first
 * discovered provider (falling back to `EXPO_PUBLIC_BLOCKS_SOCIAL_CLIENT_ID`).
 * The provider name is persisted across the web redirect via sessionStorage so
 * `finishSocialLogin` sends the correct name to `POST /auth/social/callback`.
 *
 * Web: navigates away and does not return; the provider sends the browser back
 * to the registered redirect URI with `?code&state`. Native: resolves with the
 * code/state the deep link carried, exchanged by the caller.
 */
export const startSocialLogin = async (
  provider?: SocialProvider,
): Promise<{ code: string; state: string } | undefined> => {
  // Resolve once: explicit arg → first discovered → null (env-fallback in getSocialAuthorizeUrl)
  const resolved = provider ?? (await getSocialProvider());
  const authorizeUrl = await getSocialAuthorizeUrl(resolved ?? undefined);

  // Record the flow and provider BEFORE navigating — on web this call never
  // returns, and the callback screen needs both to complete the exchange.
  rememberSsoFlow("social");
  // Remember a provider name even when discovery failed.
  //
  // `/auth/login-options` is 401 while signed out, so on a cold sign-in
  // `resolved` is null — yet the flow still proceeds, because
  // `getSocialAuthorizeUrl` falls back to the env SOCIAL_CLIENT_ID. Skipping the
  // remember here left the callback with no provider name, so
  // `finishSocialLogin` threw SsoConfigError and bounced the user back to the
  // login screen. Pressing sign-in a second time appeared to "fix" it only
  // because by then discovery could succeed.
  rememberSsoProvider(resolved?.provider ?? FALLBACK_SOCIAL_PROVIDER);

  if (isWeb) {
    window.location.assign(authorizeUrl);
    return undefined;
  }

  const result = await WebBrowser.openAuthSessionAsync(
    authorizeUrl,
    pickRedirectUri(resolved?.redirectUris ?? []),
  );
  if (result.type !== "success" || !result.url) return undefined;

  const { queryParams } = Linking.parse(result.url);
  const code = queryParams?.code;
  const state = queryParams?.state;
  if (typeof code !== "string" || typeof state !== "string") {
    throw new Error("Social redirect carried no code/state");
  }
  return { code, state };
};

/** Step 1 — fetch the authorize URL. This is a data call, never a navigation. */
const fetchAuthorizeUrl = async (): Promise<string> => {
  if (!isSsoConfigured()) {
    throw new SsoConfigError(
      "Blocks SSO is not configured — set EXPO_PUBLIC_BLOCKS_OIDC_CLIENT_ID (and EXPO_PUBLIC_X_BLOCKS_KEY / API URL).",
    );
  }

  const redirectUri = getRedirectUri();
  // x-blocks-key must be sent in BOTH the query string and the header.
  const url =
    `${BLOCKS_ENDPOINTS.ssoInitiate}` +
    `?x-blocks-key=${encodeURIComponent(PROJECT_KEY)}` +
    `&clientId=${encodeURIComponent(OIDC_CLIENT_ID)}` +
    `&redirectUri=${encodeURIComponent(redirectUri)}`;

  const res = await fetch(url, { headers: blocksHeaders() });
  if (!res.ok) {
    throw new Error(`SSO initiate failed: ${res.status}`);
  }

  const { redirect_uri: authorizeUrl } = (await res.json()) as {
    redirect_uri?: string;
  };
  if (!authorizeUrl) {
    throw new Error("SSO initiate returned no redirect_uri");
  }
  return authorizeUrl;
};

/**
 * Steps 1–3. On web this navigates away and never returns. On native it opens
 * the hosted login in an auth session and resolves with the `?code`/`state` the
 * redirect carried, which the caller hands to `finishLogin`.
 */
export const startLogin = async (): Promise<
  { code: string; state: string } | undefined
> => {
  const authorizeUrl = await fetchAuthorizeUrl();

  rememberSsoFlow("oidc");

  if (isWeb) {
    window.location.assign(authorizeUrl);
    return undefined;
  }

  const result = await WebBrowser.openAuthSessionAsync(
    authorizeUrl,
    getRedirectUri(),
  );
  if (result.type !== "success" || !result.url) {
    // "cancel" / "dismiss" — the user backed out; not an error.
    return undefined;
  }

  const { queryParams } = Linking.parse(result.url);
  const code = queryParams?.code;
  const state = queryParams?.state;
  if (typeof code !== "string" || typeof state !== "string") {
    throw new Error("SSO redirect carried no code/state");
  }
  return { code, state };
};

/**
 * Step 4 of the **social** flow — exchange the code for the session cookie.
 *
 * Contract probed live 2026-08-02:
 *
 *   POST /iam/v4/auth/social/callback  {code, state, provider}
 *
 * ⚠ The third field is the provider **name** ("microsoft"), not its clientId —
 * `{code, state, clientId}` answers 400 `provider_missing`. And this is a
 * different endpoint from `finishLogin` below: `/idp/callback` and
 * `/oidc/callback` both reject a state minted by `social/initiate`.
 *
 * Provider name resolution order:
 *   1. Explicit `providerName` argument (for direct native calls)
 *   2. Persisted name from sessionStorage / module variable (web redirect)
 *   3. First discovered provider from /auth/login-options (fallback)
 *
 * `credentials: "include"` is what lets Blocks set the session cookie.
 */
export const finishSocialLogin = async (
  code: string,
  state: string,
  providerName?: string,
): Promise<void> => {
  const provider =
    providerName ??
    getSsoProvider() ??
    (await getSocialProvider())?.provider ??
    (SOCIAL_CLIENT_ID ? FALLBACK_SOCIAL_PROVIDER : undefined);

  if (!provider) {
    throw new SsoConfigError(
      "Cannot complete social sign-in — /auth/login-options returned no provider name.",
    );
  }

  const res = await fetch(BLOCKS_ENDPOINTS.socialCallback, {
    method: "POST",
    credentials: "include",
    headers: { ...blocksHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ code, state, provider }),
  });

  if (!res.ok) {
    // The body carries `error` / `error_description` (e.g. `state_data_not_found`
    // for a stale or already-redeemed code); surface it rather than a bare status.
    const detail = await res
      .json()
      .then(
        (body: { error_description?: string; error?: string }) =>
          body?.error_description || body?.error || "",
      )
      .catch(() => "");
    throw new Error(
      `Social sign-in failed: ${detail || `HTTP ${res.status}`}`,
    );
  }

  clearSsoProvider();
  clearSsoFlow();
};

/**
 * Step 4 of the **hosted `blocks-oidc`** flow. Only reachable via `startLogin`,
 * the fallback for when no social provider is configured — see
 * `finishSocialLogin` above for the flow the app actually uses.
 */
export const finishLogin = async (
  code: string,
  state: string,
): Promise<void> => {
  const url =
    `${BLOCKS_ENDPOINTS.ssoCallback}` +
    `?code=${encodeURIComponent(code)}` +
    `&state=${encodeURIComponent(state)}`;

  const res = await fetch(url, {
    credentials: "include",
    headers: blocksHeaders(),
  });
  if (!res.ok) {
    throw new Error(`SSO callback failed: ${res.status}`);
  }

  clearSsoFlow();
};

/**
 * Renew the short-lived session (access token lives 15 min). The refresh token is
 * an HttpOnly cookie (`rt_<app-domain>`) that JS cannot read, so this sends an
 * **empty JSON body** and lets the cookies carry the credential — Blocks rotates
 * them on success. Wire this as the 401-retry path.
 *
 * Verified 2026-07-29: `POST /iam/v4/auth/refresh` with `{}` and the session
 * cookies → 200. The previous implementation posted a form-encoded
 * `grant_type=refresh_token` to `/iam/v4/oidc/token`, which returns **401** even
 * with valid cookies ("refresh token not found" without them), so the 401-retry
 * path could never recover an expiry.
 */
export const refreshSession = async (): Promise<void> => {
  const res = await fetch(BLOCKS_ENDPOINTS.authRefresh, {
    method: "POST",
    credentials: "include",
    headers: { ...blocksHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw new Error(`Session refresh failed: ${res.status}`);
  }
  // The session cookie has just been rotated; whatever identity was held
  // belongs to the old one.
  clearMeCache();
};

/** Revokes and clears the session cookies this flow set. */
export const ssoLogout = async (): Promise<void> => {
  await fetch(BLOCKS_ENDPOINTS.logout, {
    method: "POST",
    credentials: "include",
    headers: { ...blocksHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
};
